/**
 * RealTimeClickFraudDetectionPage — Trackier-style real-time click fraud detection dashboard.
 *
 * Operator surface for /api/v1/real-time-click-fraud-detection (feature #436).
 *
 * Layout:
 *   1. Topbar — title + live pulse indicator (green dot, blinking) + "Updated Xs ago".
 *   2. Four metric tiles:
 *        - Flagged (last min)
 *        - Flagged (last hour)
 *        - Top source IP
 *        - Block rate (%)
 *   3. Live feed table: ts / ip / campaign(click_id) / reason / risk_score / action.
 *        - Risk score >= 70 renders in red-600.
 *        - verdict=fraud -> "blocked" action pill (red), suspicious -> "review" (amber),
 *          clean -> "allowed" (emerald).
 *   4. Rule chips row: bot-UA, geo-mismatch, velocity, proxy — each clickable filter
 *      that narrows the live feed by substring match on `reasons`.
 *
 * Brand: emerald->sky gradient headline, Inter, white bg, zinc-200 borders,
 * 6-8px rounding, light theme. Red-600 ONLY for fraud scores / blocked pills.
 * Refetch interval: 5000ms (via useRealTimeClickFraudDetectionQuery).
 *
 * File is monolithic on purpose — owned-file contract forbids external helpers
 * outside this dir. Inline SVG icons only. No emoji. No dark mode.
 */

import { useMemo, useState, useEffect } from "react";
import { useRealTimeClickFraudDetectionQuery } from "./hooks";
import type { FraudEventRow } from "./api";

// ---------------------------------------------------------------------------
// Style tokens
// ---------------------------------------------------------------------------

const CARD =
	"rounded-[8px] border border-zinc-200 bg-white shadow-[0_1px_3px_0_rgb(24_24_27/0.08)]";
const TH =
	"px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-zinc-500";
const TD = "px-3 py-2.5 text-sm text-zinc-900";
const CHIP_BASE =
	"inline-flex items-center gap-1.5 rounded-[6px] border px-2.5 py-1 text-xs font-medium transition-colors cursor-pointer select-none";
const CHIP_INACTIVE =
	"border-zinc-200 bg-white text-zinc-700 hover:border-zinc-300 hover:bg-zinc-50";
const CHIP_ACTIVE =
	"border-emerald-500 bg-emerald-50 text-emerald-700";

// ---------------------------------------------------------------------------
// Inline SVG icons
// ---------------------------------------------------------------------------

function IconShield(props: { className?: string }) {
	return (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth={1.8}
			strokeLinecap="round"
			strokeLinejoin="round"
			className={props.className ?? "h-4 w-4"}
			aria-hidden="true"
		>
			<path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z" />
		</svg>
	);
}

function IconBolt(props: { className?: string }) {
	return (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth={1.8}
			strokeLinecap="round"
			strokeLinejoin="round"
			className={props.className ?? "h-4 w-4"}
			aria-hidden="true"
		>
			<path d="M13 2 3 14h9l-1 8 10-12h-9Z" />
		</svg>
	);
}

function IconGlobe(props: { className?: string }) {
	return (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth={1.8}
			strokeLinecap="round"
			strokeLinejoin="round"
			className={props.className ?? "h-4 w-4"}
			aria-hidden="true"
		>
			<circle cx="12" cy="12" r="10" />
			<path d="M2 12h20" />
			<path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10Z" />
		</svg>
	);
}

function IconGauge(props: { className?: string }) {
	return (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth={1.8}
			strokeLinecap="round"
			strokeLinejoin="round"
			className={props.className ?? "h-4 w-4"}
			aria-hidden="true"
		>
			<path d="M12 14 8 10" />
			<path d="M4 13a8 8 0 1 1 16 0" />
		</svg>
	);
}

// ---------------------------------------------------------------------------
// Rule chip definitions
// ---------------------------------------------------------------------------

interface RuleChipDef {
	key: string;
	label: string;
	matchers: string[]; // substrings checked against lowercased `reasons[]`
}

const RULE_CHIPS: RuleChipDef[] = [
	{ key: "bot-ua", label: "Bot UA", matchers: ["headless_bot", "tor_ua"] },
	{ key: "geo-mismatch", label: "Geo mismatch", matchers: ["geo_mismatch", "country"] },
	{ key: "velocity", label: "Velocity", matchers: ["velocity", "rate"] },
	{ key: "proxy", label: "Proxy / VPN", matchers: ["vpn_ip", "proxy", "datacenter_ip"] },
];

// ---------------------------------------------------------------------------
// Helpers (pure)
// ---------------------------------------------------------------------------

function secondsAgo(nowSec: number, ts: number): number {
	return Math.max(0, nowSec - ts);
}

function formatAgo(secs: number): string {
	if (secs < 5) return "just now";
	if (secs < 60) return `${secs}s ago`;
	if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
	return `${Math.floor(secs / 3600)}h ago`;
}

function formatTime(ts: number): string {
	try {
		return new Date(ts * 1000).toLocaleTimeString(undefined, {
			hour: "2-digit",
			minute: "2-digit",
			second: "2-digit",
		});
	} catch {
		return `${ts}`;
	}
}

function topSourceIp(rows: FraudEventRow[]): { ip: string; count: number } | null {
	if (rows.length === 0) return null;
	const counts = new Map<string, number>();
	for (const r of rows) {
		counts.set(r.ip, (counts.get(r.ip) ?? 0) + 1);
	}
	let best: { ip: string; count: number } | null = null;
	for (const [ip, count] of counts) {
		if (!best || count > best.count) best = { ip, count };
	}
	return best;
}

function rowMatchesChips(row: FraudEventRow, activeChips: Set<string>): boolean {
	if (activeChips.size === 0) return true;
	const lower = row.reasons.map((r) => r.toLowerCase());
	for (const chipKey of activeChips) {
		const def = RULE_CHIPS.find((c) => c.key === chipKey);
		if (!def) continue;
		const hit = def.matchers.some((m) => lower.some((r) => r.includes(m)));
		if (hit) return true;
	}
	return false;
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function LivePulse(props: { active: boolean; updatedAgo: string }) {
	return (
		<div
			className="inline-flex items-center gap-2 rounded-[999px] border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-700"
			data-testid="live-pulse"
		>
			<span className="relative inline-flex h-2 w-2">
				{props.active && (
					<span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
				)}
				<span
					className={`relative inline-flex h-2 w-2 rounded-full ${
						props.active ? "bg-green-500" : "bg-zinc-400"
					}`}
				/>
			</span>
			<span>LIVE</span>
			<span className="text-emerald-600/70">· Updated {props.updatedAgo}</span>
		</div>
	);
}

function MetricTile(props: {
	label: string;
	value: string;
	sub?: string;
	tone?: "emerald" | "red" | "sky" | "zinc";
	icon: React.ReactNode;
}) {
	const toneClasses = {
		emerald: "text-emerald-700",
		red: "text-red-600",
		sky: "text-sky-700",
		zinc: "text-zinc-900",
	} as const;
	const tone = toneClasses[props.tone ?? "zinc"];
	return (
		<div className={`${CARD} flex flex-col gap-2 p-5`} data-testid={`tile-${props.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
			<div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-zinc-500">
				<span className={tone}>{props.icon}</span>
				<span>{props.label}</span>
			</div>
			<div className={`text-2xl font-semibold tabular-nums ${tone}`}>
				{props.value}
			</div>
			{props.sub && <div className="text-xs text-zinc-500">{props.sub}</div>}
		</div>
	);
}

function VerdictPill(props: { verdict: FraudEventRow["verdict"] }) {
	const map = {
		fraud: "border-red-200 bg-red-50 text-red-700",
		suspicious: "border-amber-200 bg-amber-50 text-amber-700",
		clean: "border-emerald-200 bg-emerald-50 text-emerald-700",
	} as const;
	const labels = {
		fraud: "blocked",
		suspicious: "review",
		clean: "allowed",
	} as const;
	return (
		<span
			className={`inline-flex items-center rounded-[6px] border px-2 py-0.5 text-xs font-medium ${map[props.verdict]}`}
		>
			{labels[props.verdict]}
		</span>
	);
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export function RealTimeClickFraudDetectionPage() {
	const query = useRealTimeClickFraudDetectionQuery();
	const rows: FraudEventRow[] = useMemo(() => query.data?.items ?? [], [query.data]);
	const [activeChips, setActiveChips] = useState<Set<string>>(new Set());

	// Tick the "updated Xs ago" label once per second independent of the query.
	const [nowSec, setNowSec] = useState<number>(() => Math.floor(Date.now() / 1000));
	useEffect(() => {
		const id = setInterval(() => setNowSec(Math.floor(Date.now() / 1000)), 1000);
		return () => clearInterval(id);
	}, []);

	// Derived metrics over the full feed.
	const minuteCut = nowSec - 60;
	const hourCut = nowSec - 3600;
	const flaggedLastMin = useMemo(
		() => rows.filter((r) => r.created_at >= minuteCut && r.verdict !== "clean").length,
		[rows, minuteCut],
	);
	const flaggedLastHr = useMemo(
		() => rows.filter((r) => r.created_at >= hourCut && r.verdict !== "clean").length,
		[rows, hourCut],
	);
	const topIp = useMemo(() => topSourceIp(rows), [rows]);
	const blockRate = useMemo(() => {
		if (rows.length === 0) return 0;
		const blocked = rows.filter((r) => r.verdict === "fraud").length;
		return Math.round((blocked / rows.length) * 100);
	}, [rows]);

	const dataUpdatedAtSec = query.dataUpdatedAt
		? Math.floor(query.dataUpdatedAt / 1000)
		: nowSec;
	const updatedAgo = formatAgo(secondsAgo(nowSec, dataUpdatedAtSec));

	const visibleRows = useMemo(
		() => rows.filter((r) => rowMatchesChips(r, activeChips)),
		[rows, activeChips],
	);

	function toggleChip(key: string) {
		setActiveChips((prev) => {
			const next = new Set(prev);
			if (next.has(key)) next.delete(key);
			else next.add(key);
			return next;
		});
	}

	return (
		<main
			data-testid="real-time-click-fraud-detection-page"
			className="mx-auto flex w-full max-w-6xl flex-col gap-6 px-6 py-8 sm:py-12"
		>
			{/* Topbar */}
			<header className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
				<div className="flex flex-col gap-1.5">
					<p className="text-xs font-semibold uppercase tracking-[0.12em] text-zinc-500">
						Anti-fraud
					</p>
					<h1 className="bg-gradient-to-r from-emerald-600 to-sky-600 bg-clip-text text-3xl font-semibold tracking-tight text-transparent sm:text-4xl">
						Real-Time Click Fraud
					</h1>
					<p className="max-w-prose text-sm text-zinc-600">
						Live verdicts on every inbound click. Datacenter IPs, headless
						bots, VPN/proxy, and Tor signals scored in ~1 ms at the edge.
					</p>
				</div>
				<LivePulse active={!query.isError} updatedAgo={updatedAgo} />
			</header>

			{/* Metric tiles */}
			<section
				className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4"
				data-testid="metric-tiles"
			>
				<MetricTile
					label="Flagged (last min)"
					value={flaggedLastMin.toLocaleString()}
					sub="verdict != clean"
					tone={flaggedLastMin > 0 ? "red" : "emerald"}
					icon={<IconBolt />}
				/>
				<MetricTile
					label="Flagged (last hour)"
					value={flaggedLastHr.toLocaleString()}
					sub="rolling 60 min window"
					tone="zinc"
					icon={<IconShield />}
				/>
				<MetricTile
					label="Top source IP"
					value={topIp ? topIp.ip : "--"}
					sub={topIp ? `${topIp.count} hits` : "no events yet"}
					tone="sky"
					icon={<IconGlobe />}
				/>
				<MetricTile
					label="Block rate"
					value={`${blockRate}%`}
					sub={`${rows.length.toLocaleString()} events total`}
					tone={blockRate >= 50 ? "red" : "emerald"}
					icon={<IconGauge />}
				/>
			</section>

			{/* Rule chips */}
			<section
				className="flex flex-wrap items-center gap-2"
				aria-label="Rule filters"
				data-testid="rule-chips"
			>
				<span className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
					Rules:
				</span>
				{RULE_CHIPS.map((def) => {
					const on = activeChips.has(def.key);
					return (
						<button
							key={def.key}
							type="button"
							aria-pressed={on}
							onClick={() => toggleChip(def.key)}
							data-testid={`rule-chip-${def.key}`}
							className={`${CHIP_BASE} ${on ? CHIP_ACTIVE : CHIP_INACTIVE}`}
						>
							<span
								className={`h-1.5 w-1.5 rounded-full ${
									on ? "bg-emerald-500" : "bg-zinc-400"
								}`}
								aria-hidden="true"
							/>
							{def.label}
						</button>
					);
				})}
				{activeChips.size > 0 && (
					<button
						type="button"
						onClick={() => setActiveChips(new Set())}
						className="ml-1 text-xs font-medium text-zinc-500 underline-offset-2 hover:text-zinc-900 hover:underline"
					>
						Clear
					</button>
				)}
			</section>

			{/* Live feed */}
			<section className={`${CARD}`} aria-label="Live click fraud feed">
				<header className="flex items-center justify-between border-b border-zinc-200 px-5 py-3">
					<h2 className="text-sm font-semibold text-zinc-900">Live feed</h2>
					<span className="text-xs text-zinc-500 tabular-nums">
						{visibleRows.length} / {rows.length} events
					</span>
				</header>

				{query.isLoading ? (
					<p
						className="px-5 py-8 text-sm text-zinc-500"
						data-testid="real-time-click-fraud-detection-loading"
					>
						Loading live feed.
					</p>
				) : query.isError ? (
					<p
						className="px-5 py-8 text-sm text-red-600"
						role="alert"
						data-testid="real-time-click-fraud-detection-error"
					>
						{query.error?.message ?? "Failed to load feed."}
					</p>
				) : rows.length === 0 ? (
					<p
						className="px-5 py-8 text-sm text-zinc-500"
						data-testid="real-time-click-fraud-detection-empty"
					>
						No click events recorded yet. Waiting for live traffic.
					</p>
				) : visibleRows.length === 0 ? (
					<p className="px-5 py-8 text-sm text-zinc-500">
						No events match the selected rule filters.
					</p>
				) : (
					<div className="overflow-x-auto">
						<table
							className="w-full border-collapse"
							data-testid="real-time-click-fraud-detection-table"
						>
							<thead className="bg-zinc-50">
								<tr>
									<th className={TH}>Time</th>
									<th className={TH}>IP</th>
									<th className={TH}>Click ID</th>
									<th className={TH}>Reasons</th>
									<th className={`${TH} text-right`}>Risk</th>
									<th className={`${TH} text-right`}>Action</th>
								</tr>
							</thead>
							<tbody className="divide-y divide-zinc-200">
								{visibleRows.map((row) => {
									const high = row.risk_score >= 70;
									return (
										<tr
											key={`${row.click_id}-${row.created_at}`}
											data-testid={`feed-row-${row.click_id}`}
											className="hover:bg-zinc-50"
										>
											<td className={`${TD} tabular-nums text-zinc-600`}>
												{formatTime(row.created_at)}
											</td>
											<td className={`${TD} font-mono text-xs`}>{row.ip}</td>
											<td className={`${TD} font-mono text-xs text-zinc-700`}>
												{row.click_id}
											</td>
											<td className={`${TD} text-xs`}>
												<div className="flex flex-wrap gap-1">
													{row.reasons.length === 0 ? (
														<span className="text-zinc-400">—</span>
													) : (
														row.reasons.map((r) => (
															<span
																key={r}
																className="inline-flex items-center rounded-[4px] border border-zinc-200 bg-zinc-50 px-1.5 py-0.5 font-mono text-[10px] text-zinc-700"
															>
																{r}
															</span>
														))
													)}
												</div>
											</td>
											<td
												className={`${TD} text-right tabular-nums font-semibold ${
													high ? "text-red-600" : "text-zinc-900"
												}`}
												data-testid={`risk-${row.click_id}`}
											>
												{row.risk_score}
											</td>
											<td className={`${TD} text-right`}>
												<VerdictPill verdict={row.verdict} />
											</td>
										</tr>
									);
								})}
							</tbody>
						</table>
					</div>
				)}
			</section>
		</main>
	);
}

export default RealTimeClickFraudDetectionPage;
